import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  RefreshCw,
  RotateCcw,
  LogIn,
  Plus,
  X,
  Trash2,
  Pencil,
} from "lucide-react";

//For user backend data storage

const TOKEN_KEY = "ct_login_token";
const USER_KEY = "ct_login_user";

function getLoginUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

function getAccountsStorageKey() {
  const user = getLoginUser();
  return user?.id ? `ct_accounts_${user.id}` : "ct_accounts_guest";
}


// ✅ FIX 1: emptyForm outside component so it's never recreated
const emptyForm = {
  broker: "Dhan",
  loginMethod: "",
  userId: "",
  nickname: "",
  accessToken: "",
  proxyHost: "",
  proxyPort: "443",
  proxyUser: "",
  proxyPass: "",
};

const loginMethods = {
  Dhan: ["Dhan Access Token", "Dhan Automated Access Token Generation"],
  Zerodha: [
    "New Web Session",
    "Python Trader API",
    "Existing Web Session",
    "Kite API New Session",
    "Kite API with Token",
  ],
  Upstox: ["New Session", "Existing Token"],
};

const methodNeedsToken = (method) =>
  method === "" || method.toLowerCase().includes("token");

export default function Accounts() {

  //for user backend data

  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);


  const [orderSummary, setOrderSummary] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [fetchingName, setFetchingName] = useState(false);
  const loginToken = localStorage.getItem(TOKEN_KEY);
  const loginUser = getLoginUser();
  const isAdmin = loginUser?.role === "admin";
  const accountsStorageKey = getAccountsStorageKey();



// Web Socket Connection --

  useEffect(() => {
  if (!accounts || accounts.length === 0) return;

  const ws = new WebSocket(
    `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/ws`
  );

  ws.onopen = () => {
    console.log("Accounts WS connected");

    const payload = accounts
  .filter(acc => acc.accessToken && (acc.id || acc.clientId))
  .map(acc => ({
    clientId: acc.id || acc.clientId,
    accessToken: acc.accessToken
  }));

    ws.send(JSON.stringify({
      action: "subscribe",
      accounts: payload
    }));
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);

      if (data.type === "ORDER_UPDATE") {
        handleOrderUpdate(data);
      }

    } catch (e) {
      console.log("WS parse error", e);
    }
  };

  ws.onclose = () => console.log("Accounts WS closed");
  const handleOrderUpdate = (order) => {
  setOrderSummary(prev => {
    const clientId = order.clientId;

    const existing = prev[clientId] || {
      completed: 0,
      open: 0,
      cancelled: 0,
      rejected: 0,
    };

    const updated = { ...existing };

    const status = (order.status || "").toUpperCase();

    if (status.includes("TRADED") || status.includes("COMPLETE") || status.includes("FILLED")) {
      updated.completed += 1;
    } else if (status.includes("CANCEL")) {
      updated.cancelled += 1;
    } else if (status.includes("REJECT")) {
      updated.rejected += 1;
    } else {
      updated.open += 1;
    }

    return {
      ...prev,
      [clientId]: updated
    };
  });
};
  return () => ws.close();
}, [accounts]);

  // ── Auto-fetch real name for all brokers when credentials are entered ──
  useEffect(() => {
    if (editId) return;
    if (!form.accessToken || form.accessToken.length < 10) return;

    const fetchName = async () => {
      setFetchingName(true);
      try {
        let endpoint = "";
        let body = {};

        if (form.broker === "Dhan") {
          endpoint = "/api/dhan/profile";
          body = { accessToken: form.accessToken };
        } else if (form.broker === "Zerodha") {
          if (!form.userId) return;
          endpoint = "/api/zerodha/profile";
          body = { apiKey: form.userId, accessToken: form.accessToken };
        } else if (form.broker === "Upstox") {
          endpoint = "/api/upstox/profile";
          body = { accessToken: form.accessToken };
        }

        if (!endpoint) return;

        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.name) setForm(prev => ({ ...prev, nickname: data.name }));
      } catch {
        // silently fail — user can type nickname manually
      } finally {
        setFetchingName(false);
      }
    };

    const timer = setTimeout(fetchName, 800);
    return () => clearTimeout(timer);
  }, [form.userId, form.accessToken, form.broker, editId]);
  // ────────────────────────────────────────────────────────────

  // ✅ FIX 2: Use a ref so autoRefresh interval always sees latest accounts
  const accountsRef = useRef(accounts);
  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

// For user backend data storage

useEffect(() => {
  if (!loginToken) {
    setAccounts([]);
    setAccountsLoaded(true);
    return;
  }

  fetch("/api/login/data/accounts", {
    headers: { Authorization: `Bearer ${loginToken}` },
  })
    .then(res => res.json())
    .then(data => {
      const loaded = data.accounts || [];
      setAccounts(loaded);
      localStorage.setItem(accountsStorageKey, JSON.stringify(loaded));
      localStorage.setItem("ct_accounts", JSON.stringify(loaded));
      setAccountsLoaded(true);
    })
    .catch(err => {
      console.log("Account load failed:", err.message);
      setAccounts([]);
      setAccountsLoaded(true);
    });
}, []);


  // Save to localStorage on every accounts change
 useEffect(() => {
  if (!accountsLoaded) return;

  localStorage.setItem(accountsStorageKey, JSON.stringify(accounts));
  localStorage.setItem("ct_accounts", JSON.stringify(accounts));
  window.dispatchEvent(new Event("accountsUpdated"));

  if (!loginToken) return;

  fetch("/api/login/data/accounts", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${loginToken}`,
    },
    body: JSON.stringify({ accounts }),
  }).catch(err => console.log("Account save failed:", err.message));
}, [accounts, accountsLoaded]);


  // ── Login single account — routes by broker ─────────────────
  const loginSingleAccount = async (acc) => {
    try {
      let endpoint = "";
      let body = {};

      if (acc.broker === "Dhan") {
        endpoint = "/api/dhan/account";
        body = { userId: acc.id, accessToken: acc.accessToken };
      } else if (acc.broker === "Upstox") {
        endpoint = "/api/upstox/account";
        body = { userId: acc.id, accessToken: acc.accessToken };
      } else if (acc.broker === "Zerodha") {
        // Zerodha account route — add when ready
        return { ...acc, status: "Not Supported Yet" };
      } else {
        return { ...acc, status: "Unknown Broker" };
      }

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      return {
        ...acc,
        balance: Number(data.balance) || 0,
        pnl: Number(data.pnl) || 0,
        positions: data.positions || 0,
        orders: data.orders || 0,
        name: data.dhanClientId || acc.name,
        status: "Connected",
      };
    } catch {
      return { ...acc, status: "Failed" };
    }
  };

  // ✅ FIX 2: Always reads accountsRef.current — never stale
  const loginAllAccounts = useCallback(async () => {
    const current = accountsRef.current;
    if (!current.length) return;

    // ── Route all brokers through loginSingleAccount ────────
    const updated = await Promise.all(
      current.map(async (acc) => await loginSingleAccount(acc))
    );

    setAccounts(updated);
  }, []);

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loginAllAccounts, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, loginAllAccounts]);

  // RESET ALL
  const resetAllCopied = () => {
    setAccounts(prev => prev.map(acc => ({ ...acc, copied: 0 })));
  };

  // RESET SINGLE
  const resetSingle = (id) => {
    setAccounts(prev =>
      prev.map(acc => acc.id === id ? { ...acc, copied: 0 } : acc)
    );
  };

  // DELETE
  const deleteAccount = (id) => {
    setAccounts(prev => prev.filter(acc => acc.id !== id));
  };

  // TOGGLE TRACKING
  const toggleTracking = (id) => {
    setAccounts(prev =>
      prev.map(acc =>
        acc.id === id ? { ...acc, tracking: !acc.tracking } : acc
      )
    );
  };

  // ✅ FIX 1: closeModal always resets form cleanly
  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm({ ...emptyForm });
  };

  // ADD / EDIT
  const handleAddAccount = () => {
    if (!form.userId) {
      alert("User ID is required");
      return;
    }
    if (methodNeedsToken(form.loginMethod) && !form.accessToken) {
      alert("Access Token is required for this login method");
      return;
    }

    if (editId) {
      setAccounts(prev =>
        prev.map(acc =>
          acc.id === editId
            ? {
                ...acc,
                name: form.nickname || form.userId,
                accessToken: form.accessToken,
                loginMethod: form.loginMethod,
                proxyHost: form.proxyHost || "",
                proxyPort: form.proxyPort || "443",
                proxyUser: form.proxyUser || "",
                proxyPass: form.proxyPass || "",
              }
            : acc
        )
      );
    } else {
      const newAcc = {
        id: form.userId,
        name: form.nickname || form.userId,
        broker: form.broker,
        loginMethod: form.loginMethod,
        accessToken: form.accessToken,
        ownerUserId: loginUser?.id,
        ownerUsername: loginUser?.username,
        proxyHost: form.proxyHost || "",
        proxyPort: form.proxyPort || "443",
        proxyUser: form.proxyUser || "",
        proxyPass: form.proxyPass || "",

        balance: 0,
        pnl: 0,
        copied: 0,
        positions: 0,
        orders: 0,
        tracking: true,
        status: "Disconnected",
      };
      setAccounts(prev => [...prev, newAcc]);
    }

    closeModal();
  };

  // EDIT CLICK
  const handleEdit = (acc) => {
    setForm({
      broker: acc.broker || "Dhan",
      loginMethod: acc.loginMethod || "Dhan Access Token",
      userId: acc.id,
      nickname: acc.name || "",
      accessToken: acc.accessToken || "",
      proxyHost: acc.proxyHost || "",
      proxyPort: acc.proxyPort || "443",
      proxyUser: acc.proxyUser || "",
      proxyPass: acc.proxyPass || "",
    });
    setEditId(acc.id);
    setShowModal(true);
  };

  return (
    <div className="mobile-page mobile-accounts p-6">

      {/* HEADER */}
      <div className="flex justify-between mb-6">
        <h1 className="text-3xl font-bold">Account Management</h1>

        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`px-4 py-2 rounded ${autoRefresh ? "bg-blue-600" : "bg-gray-700"}`}
          >
            Auto Refresh (5s)
          </button>

          <button onClick={loginAllAccounts} className="bg-purple-600 px-4 py-2 rounded flex gap-2 items-center">
            <RefreshCw size={16} /> Refresh
          </button>

          <button
            onClick={resetAllCopied}
            className="btn-solid-green px-4 py-2 rounded flex gap-2 items-center"
            style={{
              background: "#006b49",
              color: "#ffffff",
              border: "1px solid rgba(34, 197, 94, 0.8)",
              boxShadow: "0 0 16px rgba(0, 107, 73, 0.75)",
            }}
          >
            <RotateCcw size={16} /> Reset All
          </button>

          <button onClick={loginAllAccounts} className="bg-green-600 px-4 py-2 rounded flex gap-2 items-center">
            <LogIn size={16} /> Login All
          </button>

          <button
            onClick={() => { setForm({ ...emptyForm }); setEditId(null); setShowModal(true); }}
            className="bg-blue-500 px-4 py-2 rounded flex gap-2 items-center"
          >
            <Plus size={16} /> Add Account
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="mobile-grid-table bg-[#1e293b] rounded-xl overflow-hidden">
        <div className="mobile-table-head grid grid-cols-10 p-4 text-gray-400 border-b border-gray-700">
          <span>User ID</span>
          <span>Nickname</span>
          <span>Broker</span>
          <span>Status</span>
          <span>Balance</span>
          <span>PNL</span>
          <span>Positions</span>
          <span>Orders</span>
          <span>Tracking</span>
          <span>Actions</span>
        </div>

        {accounts.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No accounts added yet. Click "Add Account" to get started.
          </div>
        )}


        {accounts.map((acc) => (
          <div key={acc.id} className="mobile-card-row grid grid-cols-10 p-4 border-b border-gray-800 items-center">
            <span>{acc.id}</span>
            <span>{acc.name}</span>
            <span>{acc.broker}</span>

            <span>
              <span className={`px-2 py-1 rounded text-sm ${
                acc.status === "Connected"
                  ? "bg-green-600"
                  : acc.status === "Failed"
                  ? "bg-red-500"
                  : "bg-gray-500"
              }`}>
                {acc.status}
              </span>
            </span>

            <span>₹{acc.balance}</span>

            <span className={acc.pnl >= 0 ? "text-green-400" : "text-red-400"}>
              ₹{acc.pnl}
            </span>

            <span>{acc.positions}</span>
            <span>{acc.orders}</span>

            <span>
              <button
                onClick={() => toggleTracking(acc.id)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  acc.tracking ? "bg-blue-500" : "bg-gray-500"
                }`}
              />
            </span>

            <span className="flex gap-3">
              <LogIn
                className="text-green-400 cursor-pointer"
                onClick={async () => {
                  const updated = await loginSingleAccount(acc);
                  setAccounts(prev =>
                    prev.map(a => a.id === acc.id ? updated : a)
                  );
                }}
              />
              <Pencil
                className="text-blue-400 cursor-pointer"
                onClick={() => handleEdit(acc)}
              />
              <RotateCcw
                className="text-orange-400 cursor-pointer"
                onClick={() => resetSingle(acc.id)}
              />
              <Trash2
                className="text-red-500 cursor-pointer"
                onClick={() => deleteAccount(acc.id)}
              />
            </span>
          </div>
        ))}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-[#1e293b] p-6 rounded-xl w-[420px]">

            <div className="flex justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {editId ? "Edit Account" : "Add Account"}
              </h2>
              <X onClick={closeModal} className="cursor-pointer" />
            </div>

            <div className="flex flex-col gap-3">

              <select
                className="p-2 rounded bg-gray-700"
                value={form.broker}
                disabled={!!editId}
                onChange={(e) =>
                  setForm({ ...form, broker: e.target.value, loginMethod: "" })
                }
              >
                <option>Dhan</option>
                <option>Zerodha</option>
                <option>Upstox</option>
              </select>

              <select
                className="p-2 rounded bg-gray-700"
                value={form.loginMethod}
                onChange={(e) =>
                  setForm({ ...form, loginMethod: e.target.value })
                }
              >
                <option value="">Select Login Method</option>
                {loginMethods[form.broker].map((m, i) => (
                  <option key={i}>{m}</option>
                ))}
              </select>

              <input
                placeholder="User ID"
                className="p-2 rounded bg-gray-700 disabled:opacity-50"
                value={form.userId}
                disabled={!!editId}
                onChange={(e) =>
                  setForm({ ...form, userId: e.target.value })
                }
              />

              <div className="relative">
                <input
                  placeholder={fetchingName ? "Fetching name..." : "Nickname (auto-fetched from broker)"}
                  className="p-2 rounded bg-gray-700 w-full pr-8"
                  value={form.nickname}
                  onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                />
                {fetchingName && (
                  <span className="absolute right-2 top-2.5 text-gray-400 text-xs animate-pulse">⏳</span>
                )}
              </div>

              {methodNeedsToken(form.loginMethod) && (
                <input
                  placeholder="Access Token"
                  className="p-2 rounded bg-gray-700"
                  value={form.accessToken}
                  onChange={(e) =>
                    setForm({ ...form, accessToken: e.target.value })
                  }
                />
              )}

            </div>

                            {/* Proxy fields for SEBI static IP compliance */}
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 8, marginBottom: 4 }}>
                STATIC IP PROXY (optional — for child accounts)
              </div>
              <input
                placeholder="Proxy Host (e.g. dc-mum-002.staticip.in)"
                className="p-2 rounded bg-gray-700"
                value={form.proxyHost || ""}
                onChange={e => setForm({ ...form, proxyHost: e.target.value })}
              />
              <input
                placeholder="Proxy Port (e.g. 443)"
                className="p-2 rounded bg-gray-700"
                value={form.proxyPort || ""}
                onChange={e => setForm({ ...form, proxyPort: e.target.value })}
              />
              <input
                placeholder="Proxy UserID"
                className="p-2 rounded bg-gray-700"
                value={form.proxyUser || ""}
                onChange={e => setForm({ ...form, proxyUser: e.target.value })}
              />
              <input
                placeholder="Proxy Password"
                className="p-2 rounded bg-gray-700"
                value={form.proxyPass || ""}
                onChange={e => setForm({ ...form, proxyPass: e.target.value })}
              />

            <div className="flex justify-end gap-3 mt-5">
              <button onClick={closeModal} className="px-4 py-2 bg-gray-600 rounded">
                Cancel
              </button>
              <button onClick={handleAddAccount} className="px-4 py-2 bg-blue-500 rounded">
                {editId ? "Update" : "Add"}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}